import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Clock, Megaphone, MessageCircle, ExternalLink, ArrowLeft, ChevronDown, ChevronUp, Building2, Paperclip, Download } from 'lucide-react';
import Pagination from '../../components/common/Pagination';
import PublicLayout from '../../components/public/PublicLayout';
import { PUBLIC_BASE } from './config';

function parseContentSections(content) {
  if (!content) return []
  const KEYS = [
    '사업개요','지원내용','지원대상','지원규모','지원조건','신청방법',
    '신청서접수기간','선정절차및평가기준','선정절차 및 평가기준','유의사항','문의처',
    '접수기간','사업기간','사업목적','사업명','모집공고 및 지원개요','모집공고및지원개요',
    '신청자격','지원규모 및 내용','참가자격','모집대상','지원내용 및 규모','사업내용',
    '주요일정','선발절차','평가기준','제출서류','기대효과','추진배경',
    '선정기준','선정방법','기타사항','세부내용','참고사항','심사기준',
    '신청서 접수기간','추진일정','추진체계','모집분야','지원분야','지원절차',
  ]
  const regex = new RegExp(`(?:^|\\n)(${KEYS.join('|')})\\s*[:：]\\s*`, 'g')
  const sections = []
  let lastIndex = 0, lastKey = null, match
  while ((match = regex.exec(content)) !== null) {
    if (lastKey !== null) {
      const value = content.slice(lastIndex, match.index).trim()
      if (value) sections.push({ key: lastKey, value })
    }
    lastKey = match[1]
    lastIndex = match.index + match[0].length
  }
  if (lastKey !== null) {
    const value = content.slice(lastIndex).trim()
    if (value) sections.push({ key: lastKey, value })
  }
  if (sections.length === 0 && content.trim())
    sections.push({ key: '공고내용', value: content.trim() })
  return sections
}

function CellValue({ value }) {
  const [expanded, setExpanded] = useState(false)
  const LIMIT = 300
  const isLong = value.length > LIMIT
  return (
    <div>
      <span className="whitespace-pre-line leading-relaxed">
        {isLong && !expanded ? value.slice(0, LIMIT) + '…' : value}
      </span>
      {isLong && (
        <button onClick={() => setExpanded(v => !v)}
          className="ml-1 inline-flex items-center gap-0.5 text-xs text-sky-400 hover:text-sky-300 font-medium">
          {expanded ? <><ChevronUp size={11} />접기</> : <><ChevronDown size={11} />더 보기</>}
        </button>
      )}
    </div>
  )
}

function formatDate(dateStr) {
  if (!dateStr || dateStr.length !== 8) return dateStr;
  return `${dateStr.slice(0, 4)}.${dateStr.slice(4, 6)}.${dateStr.slice(6, 8)}`;
}

// YYYYMMDD 또는 YYYY-MM-DD 모두 처리
function normalizeDate(d) {
  if (!d) return '';
  return d.replace(/-/g, '');
}

function getStatus(startDt, endDt) {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const s = normalizeDate(startDt);
  const e = normalizeDate(endDt);
  if (s && today < s) return { label: '접수예정', color: 'bg-amber-500/15 text-amber-400 border-amber-500/20' };
  if (!e || today <= e) return { label: '접수중', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' };
  return { label: '마감', color: 'bg-slate-500/15 text-slate-500 border-slate-500/20' };
}

const SOURCE_TABS = [
  { key: 'kocca', label: '한국콘텐츠진흥원', short: 'KOCCA' },
  { key: 'mss', label: '중소벤처기업부', short: 'MSS' },
];

const TABS = [
  { key: 'notice', label: '공지사항', icon: Megaphone },
  { key: 'support', label: '지원사업', icon: Briefcase, active: true },
  { key: 'free', label: '자유게시판', icon: MessageCircle },
];

function PublicSupportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const detailId = searchParams.get('id');
  const urlPage = parseInt(searchParams.get('page')) || 1;
  const urlSource = searchParams.get('source') || 'kocca';

  const [source, setSource] = useState(urlSource);
  const [items, setItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(urlPage);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(false);
  const limit = 15;

  // 목록 데이터 로드
  useEffect(() => {
    if (detailId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        setError(false);
        const endpoint = source === 'mss' ? '/api/mss/support' : '/api/kocca/support';
        const res = await axios.get(endpoint, {
          params: { pageNo: currentPage, numOfRows: limit },
          timeout: 20000,
        });
        setItems(res.data.data || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      } catch (err) {
        console.error('지원사업 로딩 실패:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentPage, detailId, source]);

  // URL에 id가 있으면 상세 로드
  useEffect(() => {
    if (!detailId) {
      setSelected(null);
      return;
    }
    const fetchDetail = async () => {
      setDetailLoading(true);
      try {
        const detailSource = searchParams.get('source') || 'kocca';
        const endpoint = detailSource === 'mss'
          ? `/api/mss/support/${encodeURIComponent(detailId)}`
          : `/api/kocca/support/${encodeURIComponent(detailId)}`;
        const res = await axios.get(endpoint);
        setSelected({ ...res.data, _source: detailSource });
      } catch {
        setSelected(null);
      } finally {
        setDetailLoading(false);
      }
    };
    fetchDetail();
  }, [detailId]);

  const openDetail = (item) => {
    const params = { id: item.id, page: currentPage };
    if (source !== 'kocca') params.source = source;
    setSearchParams(params);
  };

  const backToList = () => {
    const params = currentPage > 1 ? { page: currentPage } : {};
    if (source !== 'kocca') params.source = source;
    setSearchParams(params);
  };

  const switchSource = (newSource) => {
    setSource(newSource);
    setCurrentPage(1);
    setItems([]);
  };

  const displayNumber = (index) => total - (currentPage - 1) * limit - index;

  // 상세 뷰
  if (detailId && selected) {
    const status = getStatus(selected.startDate, selected.endDate);
    const sourceName = selected._source === 'mss' ? '중소벤처기업부' : '한국콘텐츠진흥원';
    const sourceDomain = selected._source === 'mss' ? 'mss.go.kr' : 'kocca.kr';
    const originUrl = selected.link || selected.viewUrl;
    const sections = parseContentSections(selected.content || '');

    const infoRows = [
      { label: '신청기간', value: (selected.startDate || selected.endDate) ? `${formatDate(selected.startDate) || '미정'} ~ ${formatDate(selected.endDate) || '미정'}` : null },
      { label: '담당자', value: selected.manager ? `${selected.manager}${selected.department ? ` (${selected.department})` : ''}` : null },
      { label: '연락처', value: selected.phone || null },
      { label: '등록일', value: selected.regDate ? formatDate(selected.regDate) : null },
      ...sections.map(s => ({ label: s.key, value: s.value, isContent: true })),
    ].filter(r => r.value);

    return (
      <PublicLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <button
            onClick={backToList}
            className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors mb-6">
            <ArrowLeft size={16} /> 목록으로
          </button>

          {/* 헤더 */}
          <div className="bg-[#182334] rounded-xl border border-white/[0.08] p-6 mb-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${status.color}`}>{status.label}</span>
                  <span className="inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-medium border-violet-500/20 bg-violet-500/10 text-violet-400">{sourceName}</span>
                  {selected.category && (
                    <span className="inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-medium border-slate-500/20 bg-slate-500/10 text-slate-400">{selected.category}</span>
                  )}
                </div>
                <h1 className="text-lg font-bold text-white mb-2 leading-relaxed">{selected.title}</h1>
                <div className="flex flex-wrap gap-4 text-sm text-slate-400">
                  <span className="flex items-center gap-1"><Building2 size={14} /> {sourceName}</span>
                  {(selected.startDate || selected.endDate) && (
                    <span className="flex items-center gap-1"><Clock size={14} /> 마감 {formatDate(selected.endDate) || '미정'}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* 정보 + 본문 통합 테이블 */}
            {infoRows.length > 0 && (
              <div className="bg-[#182334] rounded-xl border border-white/[0.08] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs sm:text-sm">
                    <tbody>
                      {infoRows.map((row, i) => (
                        <tr key={i} className="border-b border-white/[0.05] last:border-0">
                          <td className="px-2 sm:px-4 py-1.5 sm:py-3 bg-white/[0.04] font-medium text-slate-400 w-24 sm:w-32 whitespace-nowrap align-top">
                            {row.label}
                          </td>
                          <td className="px-2 sm:px-4 py-1.5 sm:py-3 text-slate-200">
                            {row.isContent ? (
                              <CellValue value={row.value} />
                            ) : row.label === '연락처' ? (
                              <a href={`tel:${row.value}`} className="text-sky-400 hover:underline">{row.value}</a>
                            ) : (
                              <span className="whitespace-nowrap">{row.value}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 첨부파일 */}
            {selected.attachments?.length > 0 && (
              <div className="bg-[#182334] rounded-xl border border-white/[0.08] p-5">
                <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-1">
                  <Paperclip size={14} /> 첨부파일
                </h3>
                <div className="space-y-2">
                  {selected.attachments.map((file, i) => (
                    <a key={i} href={file.url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300">
                      <Download size={14} /> {file.name}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* 원문 링크 */}
            {originUrl && (
              <a href={originUrl} target="_blank" rel="noopener noreferrer"
                className="block w-full text-center px-4 py-3 bg-[#182334] border border-white/[0.08] text-sm font-medium text-slate-300 rounded-xl hover:bg-white/[0.04] transition-colors">
                <ExternalLink size={14} className="inline mr-1" />{sourceDomain} 원문 보기
              </a>
            )}
          </div>
        </div>
      </PublicLayout>
    );
  }

  // 로딩 중 (상세)
  if (detailId && detailLoading) {
    return (
      <PublicLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  // 목록 뷰
  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center">커뮤니티</h1>
          <p className="text-center text-slate-500 text-sm mt-1">정부 지원사업 공고 정보입니다.</p>
          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            {TABS.map(({ key, label, icon: Icon, active }) => (
              <Link
                key={key}
                to={`${PUBLIC_BASE}/community/${key}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  active
                    ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
                    : 'bg-white/[0.04] text-slate-400 border border-white/[0.08] hover:bg-white/[0.08]'
                }`}>
                <Icon size={14} />
                {label}
              </Link>
            ))}
          </div>
        </div>

        {/* 기관 탭 */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {SOURCE_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => switchSource(tab.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                source === tab.key
                  ? 'bg-sky-500/20 text-sky-400 border-sky-500/30'
                  : 'bg-white/[0.03] text-slate-400 border-white/[0.08] hover:bg-white/[0.06]'
              }`}>
              {tab.label}
            </button>
          ))}
          <span className="text-sm text-slate-500 ml-auto">
            총 <span className="text-sky-400 font-semibold">{total}</span>건
          </span>
        </div>

        {/* 테이블 */}
        <div className="overflow-x-auto rounded-2xl border border-white/[0.08] shadow-sm">
          <table className="w-full table-auto text-sm">
            <thead>
              <tr className="bg-white/[0.03] border-b border-white/[0.08]">
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center w-16">번호</th>
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center hidden sm:table-cell w-24">분류</th>
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-left">제목</th>
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center hidden sm:table-cell w-24">상태</th>
                <th className="px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide text-center hidden sm:table-cell w-28">접수마감</th>
              </tr>
            </thead>
            <tbody className="bg-[#182334] divide-y divide-white/[0.05]">
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <div className="flex justify-center">
                      <div className="w-6 h-6 border-2 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Briefcase size={32} className="text-slate-600" />
                      <p className="text-slate-500 text-sm">데이터를 불러오지 못했습니다.</p>
                      <button
                        onClick={() => { setError(false); setLoading(true); const endpoint = source === 'mss' ? '/api/mss/support' : '/api/kocca/support'; axios.get(endpoint, { params: { pageNo: currentPage, numOfRows: limit }, timeout: 20000 }).then(res => { setItems(res.data.data || []); setTotal(res.data.total || 0); setTotalPages(res.data.totalPages || 1); }).catch(() => setError(true)).finally(() => setLoading(false)); }}
                        className="px-4 py-1.5 rounded-lg text-xs font-medium bg-sky-500/20 text-sky-400 border border-sky-500/30 hover:bg-sky-500/30 transition-colors">
                        다시 시도
                      </button>
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Briefcase size={32} className="text-slate-600" />
                      <p className="text-slate-500 text-sm">등록된 지원사업이 없습니다.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item, index) => {
                  const status = getStatus(item.startDate, item.endDate);
                  return (
                    <tr
                      key={item.id}
                      className="hover:bg-white/[0.03] transition-colors cursor-pointer"
                      onClick={() => openDetail(item)}>
                      <td className="px-3 py-3 text-center text-slate-500 text-xs">{displayNumber(index)}</td>
                      <td className="px-3 py-3 text-center hidden sm:table-cell">
                        {item.category ? (
                          <span className="inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium border-sky-500/20 bg-sky-500/10 text-sky-400">
                            {item.category}
                          </span>
                        ) : item.department ? (
                          <span className="inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium border-indigo-500/20 bg-indigo-500/10 text-indigo-400 truncate max-w-[80px]">
                            {item.department}
                          </span>
                        ) : <span className="text-slate-600">-</span>}
                      </td>
                      <td className="px-4 py-3 text-left">
                        <span className="text-sm text-slate-200 hover:text-white">{item.title}</span>
                      </td>
                      <td className="px-3 py-3 text-center hidden sm:table-cell">
                        <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center text-slate-500 text-xs hidden sm:table-cell">
                        {formatDate(item.endDate)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={(page) => { setCurrentPage(page); if (page > 1) setSearchParams({ page }); else setSearchParams({}); }} />

        <div className="mt-6 text-center">
          <p className="text-[11px] text-slate-600">출처: 한국콘텐츠진흥원(KOCCA) Open API</p>
        </div>
      </div>
    </PublicLayout>
  );
}

export default PublicSupportPage;
